import { useState } from "react";
import DatePickerField from "../molecules/DatePickerField";
import CalendarButton from "../atoms/CalendarButton";
import ActionButton from "../atoms/ActionButton";

// YYYYMMDD 文字列 → Date オブジェクト
const parseYYYYMMDD = (str) =>
  new Date(
    parseInt(str.slice(0, 4), 10),
    parseInt(str.slice(4, 6), 10) - 1,
    parseInt(str.slice(6, 8), 10),
  );

// Date オブジェクト → YYYYMMDD 文字列
const formatYYYYMMDD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
};

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

/**
 * RecordUnlockForm - レコード単位の解除登録フォーム
 *
 * 締め切り後に入力ミス等の個別修正が必要になった場合、店舗・レジ・日付を1件指定して
 * カットオフ判定の例外（解除）を登録する。旬単位の一括解除（LockPeriodForm側）とは異なり、
 * 指定した1件だけが編集可能になり、他の店舗・レジ・日付には影響しない。
 *
 * @param {object[]}  regiList   - 店舗・レジ一覧（{ ShopNo, ShopName, RegiNo, RegiName }）
 * @param {function}  onRegister - ({ shopNo, regiNo, yyyymmdd }) => Promise を呼ぶ登録ハンドラ
 * @param {boolean}   disabled   - 登録処理中の非活性フラグ
 */
function RecordUnlockForm({ regiList, onRegister, disabled }) {
  const [shopNo, setShopNo] = useState("");
  const [regiNo, setRegiNo] = useState("");
  const [date, setDate] = useState(formatYYYYMMDD(new Date()));
  const [confirming, setConfirming] = useState(false);

  const selected = regiList.find(
    (r) => String(r.ShopNo) === shopNo && String(r.RegiNo) === regiNo,
  );
  const canSubmit = shopNo !== "" && regiNo !== "" && date !== "";

  const handleSelectChange = (e) => {
    const [s, r] = e.target.value.split("-");
    setShopNo(s ?? "");
    setRegiNo(r ?? "");
  };

  const handleExecute = async () => {
    await onRegister({ shopNo: Number(shopNo), regiNo: Number(regiNo), yyyymmdd: date });
    setConfirming(false);
  };

  return (
    <div className="border border-gray-300 rounded p-4 mb-6">
      <h2 className="text-base font-medium mb-1">レコード単位の解除</h2>
      <p className="text-sm text-gray-600 mb-3">
        締め切り後に入力ミス等の個別修正が必要な場合に使用する。指定した店舗・レジ・日付の1件だけが編集可能になり、他には影響しない。
      </p>
      <div className="flex items-center gap-4 mb-3">
        <select
          value={shopNo !== "" && regiNo !== "" ? `${shopNo}-${regiNo}` : ""}
          onChange={handleSelectChange}
          className="border border-gray-400 rounded px-2 py-2 text-base"
        >
          <option value="" disabled>
            店舗・レジを選択
          </option>
          {regiList.map((r) => (
            <option key={`${r.ShopNo}-${r.RegiNo}`} value={`${r.ShopNo}-${r.RegiNo}`}>
              {r.ShopName} {r.RegiName}
            </option>
          ))}
        </select>
        <DatePickerField
          selected={parseYYYYMMDD(date)}
          onChange={(d) => setDate(formatYYYYMMDD(d))}
          customInput={<CalendarButton yyyyValue={date} showDay />}
          dateFormat="yyyy/MM/dd"
          locale="ja"
          dateFormatCalendar="yyyy MMMM"
          calendarClassName="datepicker-date-picker"
          popperPlacement="bottom-start"
          popperProps={{ strategy: "fixed" }}
          enableTabLoop={false}
        />
      </div>

      {!confirming ? (
        <ActionButton
          label="解除する"
          onClick={() => setConfirming(true)}
          disabled={disabled || !canSubmit}
          paddingClass="px-6 py-2"
          textSizeClass="text-base"
        />
      ) : (
        <div className="bg-gray-50 border border-gray-300 rounded p-4">
          <p className="text-base text-gray-700 mb-4">
            {selected?.ShopName} {selected?.RegiName} の {formatDateDisplay(date)} のみを解除します。
            <br />
            他の日付・店舗・レジには影響しません。よろしいですか？
          </p>
          <div className="flex gap-4">
            <ActionButton
              label="キャンセル"
              onClick={() => setConfirming(false)}
              disabled={disabled}
              paddingClass="px-5 py-2"
              textSizeClass="text-base"
            />
            <ActionButton
              label="実行する"
              onClick={handleExecute}
              disabled={disabled}
              paddingClass="px-5 py-2"
              textSizeClass="text-base"
              colorClass="bg-gray-700 text-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordUnlockForm;
